<template>
  <div class="fc-view__series-selection">
    <div class="series-selection__selected-fund">
      <div class="series-selection__selected-fund-name">
        Fund Name: {{ selectedFund.displayName }}
      </div>
      <mds-button
        type="button"
        variation="secondary"
        class="fc-button__create-new-series"
        @click="goToCreateNewSeries"
      >Create a New Series</mds-button>
    </div>
    <template v-if="isLoading">
      <div class="loading-spinner">
        <mds-loader></mds-loader>
      </div>
    </template>
    <template v-else>
      <div class="series-selection__series-filter">
        <label for="filter-series-field">
          Search or select an existing series from the list below
        </label>
        <mds-search-field
          id="filter-series-field"
          class="filter-series-field"
          placeholder="Search"
          label="Search"
          v-model="searchFilter"
        ></mds-search-field>
      </div>
      <div class="series-selection__series-list">
        <mds-form>
          <div
            class="series-list-empty"
            v-if="!isLoading && isEmptySeriesList"
          >
            No series exist
            <mds-button
              type="button"
              variation="secondary"
              class="fc-button__create-new-series"
              @click="goToCreateNewSeries"
            >Create a New Series</mds-button>
          </div>
          <div
            class="series-list-body"
            v-else
          >
            <series-list-table
              :expanded="true"
              :seriesList="paginatedData"
              :selected-series="selectedSeries || componentData.selectedSeries"
              @series-checked="seriesCheckedHandler"
            ></series-list-table>
          </div>
          <div class="series-list-pager">
            <mds-pagination
              v-if="!isEmptySeriesList"
              show-items-info
              show-items-select
              :page="currentPage"
              :pageSizes="pageSizes"
              :pageSize="pageSize"
              :total-items="totalRows"
              @mds-pagination-page-changed="paginationConfigChanged"
            ></mds-pagination>
          </div>
          <div class="series-list-footer">
            <mds-button
              type="button"
              class="select-series-button__continue"
              variation="primary"
              :disabled="!hasSelectedSeries"
              @click="continueHandler"
            >
              Continue
            </mds-button>
          </div>
        </mds-form>
      </div>
    </template>
  </div>
</template>

<script>
import { concat, find, get } from 'lodash';
import MdsForm from '@mds/form';
import MdsSearchField from '@mds/search-field';
import MdsPagination from '@mds/pagination';
import SeriesListTable from '@/components/molecules/SeriesListTable';
import ClientFilterPaginationMixin from '@/mixins/ClientFilterPaginationMixin';
import ErrorNotificationMixin from '@/mixins/ErrorNotificationMixin';

import { mapActions, mapState } from 'vuex';
import * as TargetSeries from '@/services/TargetSeries';

const seriesFilterCallback = (searchFilter) => (item) => {
  return item.seriesName.toLowerCase().includes(searchFilter.toLowerCase());
};

const paginationConfig = {
  pageSizes: [10, 25, 50, -1],
  pageSize: 25,
};

/**
 * @Component(SeriesListSelection)
 */
export default {
  components: {
    MdsForm,
    MdsSearchField,
    MdsPagination,
    SeriesListTable,
  },
  mixins: [
    ClientFilterPaginationMixin(seriesFilterCallback, paginationConfig),
    ErrorNotificationMixin({ component: 'SeriesListSelection' }),
  ],
  data() {
    return {
      selectedSeries: null,
      searchFilter: '',
      isLoading: true,
    };
  },
  props: {
    componentData: {
      required: true,
      type: Object,
    },
  },
  created() {
    this.setIsLoading(true);
    this.getSeriesListByFiduciaryId(this.fiduciary.id)
      .then((response) => {
        this.listData = this.formatData(response.data);
        this.filteredList = this.listData;
        this.selectedSeries = this.componentData.selectedSeries;
        this.setIsLoading(false);
      })
      .catch((error) => {
        this.notifyError({
          error,
          section: 'lifecycle:created',
          data: this.selectedClient,
          notificaction: {
            title: 'Series List Selection',
            message: `Fetching the series list process has presented an error. (Error: ${error.message})`,
          },
        });
        this.setIsLoading(false);
      });
  },
  methods: {
    ...mapActions([
      'logError',
      'cleanSelectedFundsList',
    ]),
    setIsLoading(isLoading) {
      this.isLoading = isLoading;
    },
    continueHandler() {
      this.$emit('component-action', {
        name: 'selected-series-and-continue',
        selectedSeries: this.selectedSeries,
      });
      this.$emit('continue');
    },
    seriesCheckedHandler(seriesId) {
      if (seriesId) {
        this.selectedSeries = find(this.filteredList, (item) => item.id === seriesId);
        this.cleanSelectedFundsList();
      }
    },
    cleanProcess() {
      if (!this.componentData.selectedSeries) {
        this.selectedSeries = null;
      }
    },
    goToCreateNewSeries() {
      this.$emit('component-action', {
        name: 'go-to-create-new-series',
      });
    },
    getSeriesListByFiduciaryId(fiduciaryId) {
      return TargetSeries.getSeriesListByFiduciaryId(fiduciaryId);
    },
    formatData(listData) {
      const formatedListData = listData.map((series) => {
        return {
          ...series,
          belongsToSelectedClient: get(series, 'fiduciaryClientId') === get(this.selectedClient, 'id'),
        };
      });
      if (!this.newSeries) {
        return formatedListData;
      }
      return concat(this.newSeries, formatedListData);
    },
  },
  computed: {
    ...mapState({
      fiduciary: (state) => state.user.fiduciary,
    }),
    selectedFund() {
      return this.componentData.selectedFund || {};
    },
    selectedClient() {
      return this.componentData.selectedClient || {};
    },
    newSeries() {
      return (get(this.componentData.selectedSeries, 'isNew')) ? this.componentData.selectedSeries : null;
    },
    hasSelectedSeries() {
      return !!this.selectedSeries;
    },
    isEmptySeriesList() {
      return !this.listData || this.listData.length === 0;
    },
  },
};
</script>

<style lang="scss">
.fc-view__series-selection {
  min-height: 600px;
  margin: 80px 80px 40px;
}

.fc-button__create-new-series {
  box-shadow: none;
}

.series-selection__selected-fund {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: $mds-space-4-x;
}

.series-selection__series-filter {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: $mds-space-4-x;
  label {
    @include mds-level-4-heading();
  }
  .filter-series-field {
    width: 25%;
  }
}

.series-selection__series-list {
  display: flex;
  flex-direction: column;
}

.series-list-body {
  min-height: $fc-main-intr-process-main-min-heigh;
}
.series-list-empty {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-content: center;
  align-items: center;
  min-height: $fc-main-intr-process-main-min-heigh;
  .fc-button__create-new-series {
    margin-top: $mds-space-3-x;
  }
}

.series-list-footer {
  margin-top: $fc-int-process-margin-box;
  display: flex;
  justify-content: flex-end;
}
</style>

<style lang="scss" module>
:global(.series-list-pager) {
  padding-top: $fc-int-process-margin-box;
  .mds-select__open-indicator {
    bottom: 7px;
  }
}
</style>
